/**
 * /ems — EMS Box (Табаки). Стиль brandbook /forecast: скляні .tile з тінню,
 * Urbanist-числа, монохромні БІЛІ Lucide-піктограми, пастельні акценти з палітри.
 * ДОГМА: червоний (--accent/--err) — ЛИШЕ помилка/збій. Генерація/статуси — зелений/пастель.
 * Дані — GET /ems/:station/live (SunSpec через EMS Box). TEST STAND only (VITE_FEATURE_EMS).
 */
import { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { API_BASE_URL, api } from '@/services/api';
import {
  Sun, BatteryCharging, BatteryLow, Zap, Gauge, Activity, Cpu, Radio,
  Pause, TriangleAlert, PlugZap,
} from 'lucide-react';

interface InvRow { unit: number; pac_kw: number | null; dc_kw: number | null; st: number | null; ok: boolean; }
interface EmsLive {
  station: string; readAtUtc: string; readAtLocal: string; ageSec: number;
  availableInverters: number; expectedInverters: number;
  totalPacKw: number | null; totalPpvKw: number | null;
  pcsSocPct: number | null; pcsPacKw: number | null; pcsState: number | null;
  qualityFlag: string; isStale: boolean; staleByAge: boolean;
  perDevice: { inverters: InvRow[]; pcs: any; meters?: any[] };
  status?: string;
}

const ST: Record<number, string> = { 1: 'Off', 2: 'Sleep', 3: 'Start', 4: 'MPPT', 5: 'Throttled', 6: 'ShutDown', 7: 'Fault', 8: 'Standby' };
const AC_CAP_KW = 1000;   // 8 × 125 kW nameplate
const n1 = (v: number | null | undefined) => (v == null ? '—' : v.toFixed(1));
const n0 = (v: number | null | undefined) => (v == null ? '—' : Math.round(v).toString());

// ── Радіальний гейдж (270° дуга) з накладеним HTML-числом ──────────────────
function RadialGauge({ value, max, unit, sub, color = 'var(--ok)', size = 216 }:
  { value: number | null; max: number; unit: string; sub: string; color?: string; size?: number }) {
  const pct = value == null || max <= 0 ? 0 : Math.max(0, Math.min(1, value / max));
  const c = size / 2;
  const r = size / 2 - 26;
  const circ = 2 * Math.PI * r;
  const arc = circ * (270 / 360);
  // Градуювання: 40 рисок по дузі; кожна 5-та — мажорна. Заповнені (f<=pct) світяться кольором.
  const N = 40;
  const ticks: any[] = [];
  for (let i = 0; i <= N; i++) {
    const f = i / N;
    const major = i % 5 === 0;
    const ang = (135 + 270 * f) * Math.PI / 180;
    const ro = r + 9;
    const ri = r + (major ? 18 : 13);
    const lit = f <= pct + 1e-6;
    ticks.push(<line key={i}
      x1={c + ro * Math.cos(ang)} y1={c + ro * Math.sin(ang)}
      x2={c + ri * Math.cos(ang)} y2={c + ri * Math.sin(ang)}
      stroke={lit ? color : 'var(--line-strong)'} strokeWidth={major ? 2.4 : 1.2} strokeLinecap="round"
      opacity={lit ? 0.95 : 0.5} style={{ transition: 'stroke .5s' }} />);
  }
  // Мітки шкали 0 та max (МВт) біля кінців дуги
  const endLabel = (f: number, txt: string) => {
    const ang = (135 + 270 * f) * Math.PI / 180;
    const rl = r + 30;
    return <text x={c + rl * Math.cos(ang)} y={c + rl * Math.sin(ang)} textAnchor="middle" dominantBaseline="middle"
      fontSize={11} fill="var(--text-dim)">{txt}</text>;
  };
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block', overflow: 'visible' }}>
        <g transform={`rotate(135 ${c} ${c})`}>
          <circle cx={c} cy={c} r={r} fill="none" stroke="var(--line-strong)" strokeWidth={9}
            strokeDasharray={`${arc} ${circ}`} strokeLinecap="round" opacity={0.4} />
          <circle cx={c} cy={c} r={r} fill="none" stroke={color} strokeWidth={9}
            strokeDasharray={`${arc * pct} ${circ}`} strokeLinecap="round"
            style={{ transition: 'stroke-dasharray .7s cubic-bezier(.4,0,.2,1)', filter: `drop-shadow(0 0 7px ${color})` }} />
        </g>
        {ticks}
        {endLabel(0, '0')}
        {endLabel(1, `${max / 1000}`)}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span className="font-display" style={{ fontSize: size * 0.23, fontWeight: 600, lineHeight: 1, color: 'var(--text)' }}>
          {value == null ? '—' : n0(value)}
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>{unit}</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, textAlign: 'center' }}>{sub}</span>
      </div>
    </div>
  );
}

// ── Дві плоскі ізо-панелі зі зміщенням одна відносно одної, БІЛЕ сяйво, без ніжок ──
function IsoSolarPanel({ frac }: { frac: number }) {
  const cols = 6, rows = 4, cw = 42, ch = 21;
  const glow = 0.14 + 0.5 * Math.max(0, Math.min(1, frac));
  const layer = (ox: number, oy: number, op: number, pfx: string) => {
    const proj = (i: number, j: number) => [ox + (i - j) * cw / 2, oy + (i + j) * ch / 2] as const;
    const out: any[] = [];
    const A = proj(0, 0), B = proj(cols, 0), C = proj(cols, rows), D = proj(0, rows);
    out.push(<polygon key={`${pfx}-f`} points={`${A} ${B} ${C} ${D}`} fill="var(--bg)" fillOpacity={op * 0.92} stroke="var(--line-strong)" strokeWidth={1} strokeOpacity={op} />);
    for (let i = 0; i < cols; i++) for (let j = 0; j < rows; j++) {
      const p0 = proj(i, j), p1 = proj(i + 1, j), p2 = proj(i + 1, j + 1), p3 = proj(i, j + 1);
      out.push(<polygon key={`${pfx}-${i}-${j}`} points={`${p0} ${p1} ${p2} ${p3}`}
        fill="url(#cell)" fillOpacity={op} stroke="var(--text)" strokeWidth={0.55} strokeOpacity={0.18 * op} />);
    }
    return out;
  };
  return (
    <svg viewBox="0 0 320 180" style={{ width: '100%', height: 'auto' }}>
      <defs>
        <linearGradient id="cell" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--surface-solid)" />
          <stop offset="1" stopColor="var(--bg)" />
        </linearGradient>
        <radialGradient id="glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="var(--text)" stopOpacity={glow} />
          <stop offset="1" stopColor="var(--text)" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* біле сяйво */}
      <ellipse cx="160" cy="96" rx="158" ry="82" fill="url(#glow)" />
      {/* задня панель (зміщена вправо-вгору, приглушена) */}
      {layer(196, 34, 0.5, 'back')}
      {/* передня панель (зміщена вліво-вниз, повна) */}
      {layer(132, 62, 1, 'front')}
      {/* сонце — пастельний бурштин */}
      <circle cx="300" cy="26" r="13" fill="var(--warn)" opacity={0.18 + 0.4 * glow} />
      <circle cx="300" cy="26" r="7" fill="var(--warn)" opacity={0.85} />
    </svg>
  );
}

// ── Батарея (SVG), рівень = SoC. Центрована, без бокової шкали. Розмір = h ───
function BatteryViz({ soc, charging, fault, h = 168 }: { soc: number | null; charging: boolean; fault?: boolean; h?: number }) {
  const s = soc == null ? 0 : Math.max(0, Math.min(100, soc));
  const H = 132, W = 68, fillH = (H - 12) * (s / 100);
  const col = fault ? 'var(--err)' : (s < 40 ? 'var(--warn)' : 'var(--ok)');
  const w = Math.round(h * 92 / 168);
  return (
    <svg viewBox="0 0 92 168" width={w} height={h} style={{ flexShrink: 0, display: 'block' }}>
      <rect x="34" y="2" width="24" height="9" rx="2.5" fill="var(--line-strong)" />
      <rect x="12" y="12" width={W} height={H} rx="12" fill="none" stroke="var(--line-strong)" strokeWidth="2.5" />
      <rect x="18" y={12 + (H - 6 - fillH)} width={W - 12} height={fillH} rx="7" fill={col} opacity={0.9}
        style={{ transition: 'y .7s ease, height .7s ease, fill .4s' }} />
      {charging && <path d="M50 56 L37 94 L47 94 L41 122 L59 82 L48 82 Z" fill="var(--text)" opacity={0.9} />}
    </svg>
  );
}

// ── Пряма шкала з поділками (горизонтальна лінійка). Заповнення до frac ─────
function LinearScale({ value, max, color = 'var(--ok)' }: { value: number | null; max: number; color?: string }) {
  const frac = value == null || max <= 0 ? 0 : Math.max(0, Math.min(1, value / max));
  const N = 48;                          // 48 поділок (щільніше); кожна 6-та — мажорна
  const ticks: any[] = [];
  for (let i = 0; i <= N; i++) {
    const f = i / N;
    const major = i % 6 === 0;
    const lit = f <= frac + 1e-6;
    ticks.push(<div key={i} style={{
      position: 'absolute', left: `${f * 100}%`, bottom: 0,
      width: major ? 1.6 : 1, height: major ? 15 : 7,
      background: lit ? color : 'var(--line-strong)', opacity: lit ? 0.95 : 0.4,
      transform: 'translateX(-50%)', borderRadius: 1,
      transition: 'background .5s, opacity .5s',
    }} />);
  }
  return (
    <div style={{ position: 'relative', height: 17 }}>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 1, background: 'var(--line-strong)', opacity: 0.4 }} />
      {ticks}
      {value != null && <div style={{
        position: 'absolute', left: `${frac * 100}%`, bottom: -2, transform: 'translateX(-50%)',
        width: 2.4, height: 19, background: color, borderRadius: 2,
        boxShadow: `0 0 6px ${color}`, transition: 'left .6s cubic-bezier(.4,0,.2,1)',
      }} />}
    </div>
  );
}

// ── Цифрове табло лічильника: розряди в комірках, провідні нулі приглушені ───
function MeterDigits({ value, unit, dec = 1, minInt = 3, accent = false }:
  { value: number | null; unit: string; dec?: number; minInt?: number; accent?: boolean }) {
  const litColor = accent ? 'var(--ok)' : 'var(--text)';
  const cell = (ch: string, dim: boolean, key: string) => (
    <span key={key} className="font-display" style={{
      width: 28, height: 28, boxSizing: 'border-box', borderRadius: 5,
      background: 'var(--surface-2)', border: '1px solid var(--line-strong)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 17, fontWeight: 600, lineHeight: 1,
      color: dim ? 'var(--text-dim)' : litColor, fontVariantNumeric: 'tabular-nums',
    }}>{ch}</span>
  );
  const dot = <span key="dot" style={{ fontSize: 20, fontWeight: 700, color: value == null ? 'var(--text-dim)' : litColor, padding: '0 1px' }}>.</span>;
  const cells: any[] = [];
  if (value == null) {
    for (let i = 0; i < minInt; i++) cells.push(cell('–', true, 'i' + i));
    if (dec > 0) { cells.push(dot); for (let i = 0; i < dec; i++) cells.push(cell('–', true, 'f' + i)); }
  } else {
    const [ip, dp = ''] = Math.abs(value).toFixed(dec).split('.');
    const intStr = ip.padStart(minInt, '0');
    let firstSig = intStr.length - 1;
    for (let i = 0; i < intStr.length; i++) { if (intStr[i] !== '0') { firstSig = i; break; } }
    intStr.split('').forEach((ch, i) => cells.push(cell(ch, i < firstSig, 'i' + i)));
    if (dec > 0) { cells.push(dot); dp.split('').forEach((ch, i) => cells.push(cell(ch, false, 'f' + i))); }
  }
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      {cells}
      <span style={{ fontSize: 11, color: 'var(--text-dim)', marginLeft: 5 }}>{unit}</span>
    </div>
  );
}

function SectionHead({ Icon, title, hint }: { Icon: any; title: string; hint?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 2px 12px' }}>
      <Icon size={17} strokeWidth={1.9} style={{ color: 'var(--text)' }} />
      <span className="font-display" style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{title}</span>
      {hint && <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>· {hint}</span>}
    </div>
  );
}

// ── Погодний чіп — 1:1 як на /forecast (remixicon + токени) ────────────────
function pickSky(w: any): { icon: string; label: string } {
  if (!w) return { icon: 'ri-question-line', label: '—' };
  const day = (w.shortwaveRadiation ?? 0) > 5;
  const cc = w.cloudCover ?? 0; const pr = w.precipitation ?? 0;
  if (pr >= 2.5) return { icon: 'ri-showers-line', label: 'Дощ' };
  if (pr >= 0.2) return { icon: 'ri-drizzle-line', label: 'Мряка' };
  if (cc < 15) return { icon: day ? 'ri-sun-line' : 'ri-moon-clear-line', label: 'Ясно' };
  if (cc < 50) return { icon: day ? 'ri-sun-cloudy-line' : 'ri-moon-cloudy-line', label: 'Мінлива хмарність' };
  if (cc < 85) return { icon: 'ri-cloudy-line', label: 'Хмарно' };
  return { icon: 'ri-cloudy-2-line', label: 'Похмуро' };
}
const hhmm = (v: any): string | null => (!v || typeof v !== 'string' ? null : (v.indexOf('T') >= 0 ? v.slice(v.indexOf('T') + 1) : v).slice(0, 5) || null);

function WeatherChip({ weather, sun }: { weather: any; sun?: { sunrise?: string; sunset?: string } | null }) {
  const sky = pickSky(weather);
  const temp = weather?.temperature2m;
  const windMs = weather?.windSpeed10m != null ? weather.windSpeed10m / 3.6 : null;
  const sunrise = hhmm(sun?.sunrise); const sunset = hhmm(sun?.sunset);
  return (
    <div className="flex items-center gap-2" title={'Погода зараз · ' + sky.label}>
      <i className={sky.icon + ' text-base'} style={{ color: 'var(--text-muted)' }}></i>
      <span className="text-xs text-muted">{sky.label}</span>
      <span className="text-dim">·</span>
      <span className="text-sm font-semibold text-ink font-display">{temp != null ? Math.round(temp) + '°C' : '—'}</span>
      <span className="text-dim">·</span>
      <i className="ri-windy-line text-sm" style={{ color: 'var(--text-dim)' }}></i>
      <span className="text-xs text-muted">{windMs != null ? windMs.toFixed(1) + ' м/с' : '—'}</span>
      {sunrise && <><span className="text-dim">·</span><span className="text-xs text-muted">↑{sunrise}</span></>}
      {sunset && <span className="text-xs text-muted">↓{sunset}</span>}
    </div>
  );
}

const TABAKI_ASSET = '12a78662-a28d-40d5-9eb7-e83ebb158b41';

export default function EmsPage() {
  const [d, setD] = useState<EmsLive | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dayData, setDayData] = useState<any>(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/ems/tabaki/live`);
      if (!r.ok) throw new Error('HTTP ' + r.status);
      setD(await r.json()); setErr(null);
    } catch (e: any) { setErr(e.message || 'fetch error'); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const loadWeather = useCallback(async () => {
    try {
      const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Kyiv' });
      setDayData(await api.get(`/forecasting/assets/${TABAKI_ASSET}/generation-forecasts/day?date=${today}`));
    } catch { /* погода не критична */ }
  }, []);
  useEffect(() => { loadWeather(); const t = setInterval(loadWeather, 5 * 60 * 1000); return () => clearInterval(t); }, [loadWeather]);

  const nowWeather = (() => {
    const hourly = dayData?.hourly ?? [];
    if (!hourly.length) return null;
    const nowKey = new Date().toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Kyiv' });
    const cur = nowKey.slice(0, 2) + ':00';
    const match = hourly.find((h: any) => h.hour === cur && h.weather);
    return match?.weather ?? [...hourly].reverse().find((h: any) => h.weather)?.weather ?? null;
  })();

  const invs = d?.perDevice?.inverters ?? [];
  const pac = d?.totalPacKw ?? null;
  const ppv = d?.totalPpvKw ?? null;
  const soc = d?.pcsSocPct ?? null;
  const pcsKw = d?.pcsPacKw ?? null;
  const pcsEvt1 = d?.perDevice?.pcs?.evt1 ?? 0;
  const pcsEvt2 = d?.perDevice?.pcs?.evt2 ?? 0;
  const pcsFault = d?.pcsState === 99 || pcsEvt1 !== 0 || pcsEvt2 !== 0;
  const charging = !pcsFault && pcsKw != null && pcsKw < -0.05;
  const discharging = !pcsFault && pcsKw != null && pcsKw > 0.05;
  const idle = !pcsFault && !charging && !discharging;
  const loadFrac = pac != null ? pac / AC_CAP_KW : 0;
  const invMax = 125;

  const updated = d?.readAtLocal ? d.readAtLocal.slice(11, 16) : '—';
  const stale = !!d && (d.isStale || d.ageSec > 180 || d.qualityFlag === 'no_data');
  // Заморожений/несвіжий кадр = немає ЖИВОЇ генерації → показуємо чисті нулі (не фейкові заморожені значення)
  const noGen = stale;
  const dispPac = noGen ? 0 : pac;
  const dispPpv = noGen ? 0 : ppv;
  const dispLoadFrac = noGen ? 0 : loadFrac;

  const meterMeta: Record<number, { role: string; icon: any }> = {
    12: { role: 'Нетто в мережу · 10 кВ', icon: PlugZap },
    13: { role: 'Накопичувач · 0,4 кВ', icon: BatteryCharging },
    14: { role: 'Генерація ФЕС · 0,4 кВ', icon: Sun },
  };
  const liveMeters: any[] = d?.perDevice?.meters ?? [];

  return (
    <DashboardLayout breadcrumb={['EMS Box', 'Табаки']}>
      <div style={{ color: 'var(--text)' }}>
        {/* Шапка — погода (як на /forecast) + актуальний час оновлення, без рамки */}
        <div className="flex flex-wrap items-center gap-3" style={{ marginBottom: 18 }}>
          {nowWeather && <WeatherChip weather={nowWeather} sun={dayData?.sun} />}
          {nowWeather && <span className="text-dim">·</span>}
          <span style={{ fontSize: 12.5, color: stale ? 'var(--warn)' : 'var(--text-dim)' }}>оновлено {updated}</span>
          {err && <span style={{ color: 'var(--err)', fontSize: 12 }}>{err}</span>}
        </div>

        {/* ── ФЕС + УЗЕ (рівні висоти, обидві заповнені) ── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4" style={{ marginBottom: 16 }}>
          {/* ФЕС — 2/3 */}
          <div className="tile xl:col-span-2" style={{ padding: 20, display: 'flex', gap: 18, height: '100%' }}>
            {/* ліва зона: заголовок + панель + гейдж */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <SectionHead Icon={Sun} title="ФЕС · сонячна генерація" hint="Growatt MAX 125KTL3-X" />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', gap: 18, flex: 1, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 240px', minWidth: 210, maxWidth: 400, display: 'flex', alignItems: 'center' }}>
                  <IsoSolarPanel frac={dispLoadFrac} />
                </div>
                <RadialGauge value={dispPac} max={AC_CAP_KW} unit="кВт AC" sub={`з ${AC_CAP_KW / 1000} МВт power`} color="var(--ok)" size={216} />
              </div>
            </div>
            {/* колонка метрик — на всю висоту плитки */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', minWidth: 156, flexShrink: 0 }}>
              {[
                  { Icon: Zap, k: 'Генерація AC', v: n1(dispPac), u: 'кВт', c: 'var(--ok)' },
                  { Icon: Activity, k: 'DC вхід', v: n1(dispPpv), u: 'кВт', c: 'var(--text)' },
                  { Icon: Gauge, k: 'Завантаження', v: n0(dispLoadFrac * 100), u: '%', c: 'var(--text)' },
                  { Icon: Cpu, k: 'Інвертори', v: d ? `${d.availableInverters}/${d.expectedInverters}` : '—', u: '', c: 'var(--text)' },
                ].map((m) => (
                  <div key={m.k} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 12, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <m.Icon size={19} strokeWidth={1.75} style={{ color: 'var(--text)' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>{m.k}</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span className="font-display" style={{ fontSize: 23, fontWeight: 600, color: m.c, lineHeight: 1 }}>{m.v}</span>
                        {m.u && <span style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>{m.u}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          </div>

          {/* УЗЕ — 1/3 · головне SoC по центру, Стан ліворуч / Потужність праворуч */}
          <div className="tile" style={{ padding: 20, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <SectionHead Icon={BatteryCharging} title="УЗЕ · накопичувач" hint="PCS Kehua BCS500KA" />
            {/* 3 рівні секції (≈30%), кожна центрована по вертикалі й горизонталі */}
            <div style={{ display: 'flex', flex: 1, minHeight: 220, gap: 8 }}>
              {/* Секція 1 — статуси */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[
                    { key: 'charge', label: 'Заряд', Icon: BatteryCharging, color: 'var(--ok)', active: charging },
                    { key: 'discharge', label: 'Розряд', Icon: BatteryLow, color: 'var(--warn)', active: discharging },
                    { key: 'idle', label: 'Очікування', Icon: Pause, color: 'var(--text)', active: idle },
                    { key: 'fault', label: 'Аварія', Icon: TriangleAlert, color: 'var(--err)', active: pcsFault },
                  ].map((st) => (
                    <div key={st.key} style={{ display: 'flex', alignItems: 'center', gap: 9, opacity: st.active ? 1 : 0.32, transition: 'opacity .4s' }}>
                      <span style={{
                        width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: st.active ? `color-mix(in srgb, ${st.color} 18%, transparent)` : 'var(--surface-2)',
                        boxShadow: st.active ? `0 0 13px color-mix(in srgb, ${st.color} 55%, transparent)` : 'none',
                        transition: 'background .4s, box-shadow .4s',
                      }}>
                        <st.Icon size={17} strokeWidth={2} style={{ color: st.active ? st.color : 'var(--text-dim)' }} />
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 400, color: st.active ? 'var(--text)' : 'var(--text-dim)', transition: 'color .4s' }}>{st.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Секція 2 — зображення батарейки */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BatteryViz soc={soc} charging={charging} fault={pcsFault} h={220} />
              </div>
              {/* Секція 3 — числові дані: заряд SoC + потужність PCS */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, justifyContent: 'center' }}>
                    <span className="font-display" style={{ fontSize: 44, fontWeight: 700, lineHeight: 1, color: 'var(--text)' }}>{soc == null ? '—' : n0(soc)}</span>
                    <span style={{ fontSize: 18, color: 'var(--text-dim)' }}>%</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 5, letterSpacing: '.05em' }}>заряд SoC</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'center' }}>
                  <Zap size={15} strokeWidth={1.9} style={{ color: 'var(--text)' }} />
                  <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>PCS</span>
                  <span style={{ color: 'var(--text-dim)' }}>·</span>
                  <span className="font-display" style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{n1(pcsKw != null ? Math.abs(pcsKw) : null)}<span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 400 }}> кВт</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Інвертори ── */}
        <SectionHead Icon={Radio} title="Інвертори" hint={`${invs.length ? invs.filter(i => i.ok).length : 0} активних`} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" style={{ marginBottom: 18 }}>
          {invs.map((i) => {
            // немає живої генерації (заморожений кадр) → нулі + нейтральний статус
            const acv = noGen ? 0 : i.pac_kw;
            const dcv = noGen ? 0 : i.dc_kw;
            // ККД лише коли DC відчутний (>=1 кВт) і результат фізичний (<=102%); інакше «—»
            const effRaw = (dcv && dcv >= 1 && acv && acv > 0) ? (acv / dcv) * 100 : null;
            const eff = (effRaw != null && effRaw <= 102) ? effRaw : null;
            const ok = !noGen && i.st === 4;
            const stName = noGen ? 'Sleep' : (i.st != null ? (ST[i.st] ?? String(i.st)) : '—');
            return (
              <div key={i.unit} className="tile" style={{ padding: 15, display: 'flex', flexDirection: 'column', gap: 13 }}>
                {/* шапка: чіп-іконка + #N (без слова «Інвертор») · статус */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 27, height: 27, borderRadius: 8, background: 'var(--surface-2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Cpu size={15} strokeWidth={1.9} style={{ color: 'var(--text)' }} />
                    </span>
                    <span className="font-display" style={{ fontWeight: 600, fontSize: 16, color: 'var(--text)' }}>#{i.unit - 2}</span>
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: ok ? 'var(--ok)' : 'var(--text-dim)' }}>
                    <span style={{ width: 6, height: 6, borderRadius: 999, background: ok ? 'var(--ok)' : 'var(--text-dim)' }} />{stName}
                  </span>
                </div>
                {/* тіло: DC ліворуч · головне AC по центру · ККД праворуч */}
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ flex: '1 1 0', minWidth: 0, textAlign: 'left' }}>
                    <div style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>DC</div>
                    <div className="font-display" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', lineHeight: 1.2 }}>{n1(dcv)}<span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 400 }}> кВт</span></div>
                  </div>
                  <div style={{ flex: '0 0 auto', textAlign: 'center', padding: '0 2px' }}>
                    <div className="font-display" style={{ fontSize: 33, fontWeight: 600, color: 'var(--text)', lineHeight: 1 }}>{n1(acv)}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--text-dim)', marginTop: 3 }}>кВт AC</div>
                  </div>
                  <div style={{ flex: '1 1 0', minWidth: 0, textAlign: 'right' }}>
                    <div style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>ККД</div>
                    <div className="font-display" style={{ fontSize: 14, fontWeight: 600, color: eff && eff > 100 ? 'var(--warn)' : 'var(--text)', lineHeight: 1.2 }}>{eff ? n0(eff) : '—'}<span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 400 }}> %</span></div>
                  </div>
                </div>
                {/* пряма шкала з поділками */}
                <div>
                  <LinearScale value={acv} max={invMax} color={ok ? 'var(--ok)' : 'var(--text-dim)'} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                    <span>0</span><span>{invMax} кВт</span>
                  </div>
                </div>
              </div>
            );
          })}
          {invs.length === 0 && <div className="tile" style={{ padding: 16, gridColumn: '1/-1', color: 'var(--text-dim)', textAlign: 'center' }}>немає даних інверторів</div>}
        </div>

        {/* ── Лічильники комерційного обліку (через EMS Box SunSpec) ── */}
        <SectionHead Icon={Gauge} title="Лічильники · комерційний облік" hint="точки обліку" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" style={{ marginBottom: 8 }}>
          {liveMeters.map((m) => {
            const meta = meterMeta[m.unit] ?? { role: '', icon: Gauge };
            const exporting = m.p_kw != null && m.p_kw < -0.05;
            const MIcon = meta.icon;
            return (
              <div key={m.unit} className="tile" style={{ padding: 16 }}>
                {/* шапка: іконка+назва ліворуч, роль — до правого краю (один рядок) */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 12, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <MIcon size={20} strokeWidth={1.75} style={{ color: 'var(--text)' }} />
                    </div>
                    <span className="font-display" style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)' }}>{m.name}</span>
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--text-dim)', textAlign: 'right', flexShrink: 0 }}>{meta.role}</span>
                </div>
                {/* два табло — рознесені по половинах, кожне центроване */}
                <div style={{ display: 'flex', gap: 16 }}>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}>
                    <MeterDigits value={m.p_kw} unit="кВт" accent={exporting} />
                    <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{exporting ? 'Віддача в мережу' : 'Активна потужність'}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}>
                    <MeterDigits value={m.imp_kwh} unit="кВт·год" />
                    <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>Імпорт енергії</div>
                  </div>
                </div>
              </div>
            );
          })}
          {liveMeters.length === 0 && <div className="tile" style={{ padding: 16, gridColumn: '1/-1', color: 'var(--text-dim)', textAlign: 'center' }}>немає даних лічильників</div>}
        </div>

        <div style={{ marginTop: 12, color: 'var(--text-dim)', fontSize: 12 }}>
          Джерело: EMS Box (Radmirtech) через SunSpec Modbus, опитування ~60с.
        </div>
      </div>
    </DashboardLayout>
  );
}
